/* Progressive tabs: real anchors/panels remain usable without JavaScript. */
(() => {
  if (customElements.get('az-dashboard')) return;
  class AZDashboard extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.tabs = [...this.querySelectorAll('[data-tab]')];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      if (!this.tabs.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      this.list = this.querySelector('[data-tabs]');
      this.list.setAttribute('role', 'tablist');
      const orient = () => this.list.setAttribute('aria-orientation', this.mobile.matches ? 'horizontal' : 'vertical');
      orient();
      this.mobile.addEventListener('change', orient, options);
      this.reduced.addEventListener('change', () => this.animation?.cancel(), options);
      this.tabs.forEach(tab => {
        const panel = this.panels.find(item => item.id === tab.hash.slice(1));
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.tabIndex = 0;
        tab.addEventListener('click', event => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.select(tab, { updateHash: true, animate: true });
        }, options);
        tab.addEventListener('keydown', event => {
          const index = this.tabs.indexOf(tab);
          let next;
          if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = this.tabs.length - 1;
          else if (['ArrowRight', 'ArrowDown'].includes(event.key)) next = (index + 1) % this.tabs.length;
          else if (['ArrowLeft', 'ArrowUp'].includes(event.key)) next = (index + this.tabs.length - 1) % this.tabs.length;
          else return;
          event.preventDefault();
          this.select(this.tabs[next], { updateHash: true, animate: true });
          this.tabs[next].focus();
        }, options);
      });
      window.addEventListener('hashchange', () => {
        const tab = this.tabFromHash();
        if (tab) this.select(tab);
      }, options);
      const editor = this.closest('.shopify-section') || this;
      editor.addEventListener('shopify:block:select', event => {
        const tab = this.tabs.find(item => item.dataset.block === event.detail?.blockId);
        if (tab) this.select(tab);
      }, options);
      this.select(this.tabFromHash() || this.tabs.find(tab => tab.dataset.tab === this.dataset.default) || this.tabs[0]);
    }
    tabFromHash() {
      return this.tabs.find(tab => tab.hash === window.location.hash);
    }
    select(tab, { updateHash = false, animate = false } = {}) {
      if (!tab) return;
      const selected = this.panels.find(panel => panel.id === tab.hash.slice(1));
      if (!selected) return;
      this.animation?.cancel();
      this.tabs.forEach(item => {
        const active = item === tab;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach(panel => { panel.hidden = panel !== selected; });
      if (updateHash) {
        // Preserve order pagination and other query parameters.
        try { history.replaceState(history.state, '', tab.hash); } catch { /* tabs still work */ }
      }
      if (animate && this.dataset.motion === 'true' && !this.reduced.matches && selected.animate) {
        const duration = parseFloat(getComputedStyle(this).getPropertyValue('--dash-duration')) || 260;
        this.animation = selected.animate([{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration, easing: 'ease-out' });
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.animation?.cancel();
      this.controller = null;
    }
  }
  customElements.define('az-dashboard', AZDashboard);
})();
